<script setup>
import { computed, defineProps, ref, watch } from "vue";
import { useStore } from "vuex";
import { AppButton, SimpleSelect } from "@n11/so-ui-components";
import { FlexRender, getCoreRowModel, useVueTable } from "@tanstack/vue-table";
import Pagination from "@/components/Table/Pagination";
import { DEAL_TYPES } from '@/utils/enum/offerStatus';
import { DEAL_LISTING_PAGE_SIZES } from "@/utils/enum/configs";
const store = useStore();
const pageConfig =
  store.getters["common/getConfigList"][DEAL_LISTING_PAGE_SIZES];

//config apisinden data gelmemesi durumu icin default pagesizes verildi
const defaultPageSizes = {
  10: "10 Ürün",
  25: "25 Ürün",
  50: "50 Ürün",
  100: "100 Ürün",
};

const emit = defineEmits(["goBulkDealManagement", "onPageChanged", "onFilterChanged"]);

const props = defineProps({
  isLoading: {
    type: Boolean,
    required: true,
  },
  columns: {
    type: Array,
    required: true,
  },
  data: {
    type: Array,
    required: true,
  },
  pagination: {
    type: Object,
    required: true,
  },
  pageCount: {
    type: Number,
    required: true,
  },
  pageChanged: {
    type: Function,
    required: true,
  },
});

const tableDealRef = ref(null);
const dealsTable = ref(null);
const activeFilter = ref('ALL');

const handleFilterChange = (type) => { 
  activeFilter.value = type;
  emit("onFilterChanged", type === 'SPECIAL' ? { isCustomDeal: true } : {});
}

const visibleRows = computed(() => {
  return (dealsTable?.value.getRowModel().rows || []) ?? [];
});

dealsTable.value = useVueTable({
  get data() {
    return props.data;
  },
  get pageCount() {
    return props.pageCount;
  },
  get columns() { 
    return props.columns;
  },
  getCoreRowModel: getCoreRowModel(),
  manualPagination: true,
  onPaginationChange: (updater) => {
    if (typeof updater === "function") {
      setPagination(
        updater({
          pageIndex: props.pagination.page,
          pageSize: props.pagination.pageSize,
        })
      );
    } else {
      setPagination(updater);
    }
  },
  get rowCount() {
    return props.data?.length ?? 0;
  },
  state: {
    get pagination() {
      return props.pagination;
    },
  },
  meta: {
    refresh: () => {
      const currentState = dealsTable.value.getState().pagination;
      emit("onPageChanged", {
        page: currentState.page,
        pageSize: currentState.pageSize,
        refreshKey: Date.now()
      });
    },
  }
});

const isRowDisabled = (row) => {
  return row.original.dealType === DEAL_TYPES.BO_DELETED_DEAL
}

const isRowHighLighted = (row) => {
  return row.original.dealType === DEAL_TYPES.NEW_DEAL
}

const getHeaderWidthStyle = (header) => {
  const windowWidth = tableDealRef.value.offsetWidth;
  const percentage = Math.round((header.getSize() / windowWidth) * 100);
  return { width: `${percentage}%` };
};

const setPagination = ({ pageIndex, pageSize }) => {
  const updatedPagination = { ...props.pagination, page: pageIndex, pageSize };
  emit("onPageChanged", updatedPagination);
};

const clickHandler = (pageNum) => {
  dealsTable.value.setPageIndex(pageNum);
};

const getPageSizeFromConfig = () => {
  if (!pageConfig || typeof pageConfig !== "string") {
    return defaultPageSizes;
  }
  const pageConfigArray = pageConfig.split(";").filter(Boolean);
  const newPageSizes = {};

  pageConfigArray.forEach((size) => {
    newPageSizes[size] = `${size} Ürün`;
  });
  return { ...defaultPageSizes, ...newPageSizes };
};

const onSimpleSelectChange = (newSize) => {
  dealsTable.value.setPageSize(newSize);
};

watch(
  () => store.state.common.dealTableRefreshKey, 
  (newVal) => {
    if (newVal) {
      dealsTable.value.options?.meta.refresh();
    }
  }
);
</script>

<template>
  <div ref="tableDealRef" class="p-2 advanced-table-wrapper">
    <div class="table-top">
      <div class="table-top-header">
        <p class="table-top-title">Güncel Tekliflerimiz</p>
        <p class="table-top-text">
          Ürünlerinizin fiyatını düşürün, daha avantajlı komisyondan faydalanın.<br>
          Komisyon teklif işlemlerini aşağıdaki tablodan ya da Excel ile toplu olarak yapabilirsiniz.
        </p>
      </div>
      <AppButton
        secondary
        sm
        title="Toplu Teklif Yönetimine Git"
        class="table-top-btn"
        @click="emit('goBulkDealManagement')"
      >
      </AppButton>
    </div>
    <div>
    <div class="filters-wrapper">
      <AppButton
        :secondary-magenta="activeFilter === 'ALL'"
        :secondary="activeFilter !== 'ALL'"
        sm
        title="Tüm Teklifleriniz"
        class="table-top-btn active"
        @click="handleFilterChange('ALL')"
      >
      </AppButton>
      <AppButton
        :secondary-magenta="activeFilter === 'SPECIAL'"
        :secondary="activeFilter !== 'SPECIAL'"
        sm
        title="Özel Teklifleriniz"
        class="table-top-btn active"
        @click="handleFilterChange('SPECIAL')"
      >
      </AppButton>
      <div class="pagination flex items-center gap-2">
        <Pagination
          :pageCount="dealsTable.getPageCount()"
          :pageRange="5"
          :clickHandler="clickHandler"
          :firstLastButton="true"
          containerClass="button-group"
          prevLinkClass="border left rounded p-1"
          pageLinkClass="border rounded p-1"
          nextLinkClass="border right rounded p-1"
          :marginPages="0"
          :value="dealsTable.getState().pagination.page"
        >
        </Pagination>
        <div class="simple-select-wrapper">
          <p>Her Sayfada</p>
          <SimpleSelect
            :simpleSelectItems="getPageSizeFromConfig()"
            :selectedKey="dealsTable.getState().pagination.pageSize"
            openMenuPosition="top-left"
            @btnItemClick="onSimpleSelectChange(Number($event))"
          />
        </div>
      </div>
    </div>
    </div>
    <div class="table-wrapper">
      <div v-if="isLoading" class="loading">
        <img src="../../../assets/images/loading.gif" alt="Loading" />
      </div>
      <div v-else-if="visibleRows.length === 0" class="empty-table">
        <img
          src="../../../assets/images/empty-search.png"
          alt="Ürün bulunamadı"
        />
        <span class="title"
          >Bu tarih aralığında komisyon teklifi bulunmuyor.</span
        >
      </div>
      <table class="advanced-table" v-else>
        <thead>
          <tr
            v-for="headerGroup in dealsTable.getHeaderGroups()"
            :key="headerGroup.id"
          >
            <th
              v-for="header in headerGroup.headers"
              :key="header.id"
              :colSpan="header.colSpan"
              :style="[getHeaderWidthStyle(header)]"
            >
              <FlexRender
                v-if="!header.isPlaceholder"
                :render="header.column.columnDef.header"
                :props="header.getContext()"
              />
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in visibleRows" :key="row.id" :class="{ 'disabled': isRowDisabled(row), 'highlighted': isRowHighLighted(row) }">
            <td v-for="cell in row.getVisibleCells()" :key="cell.id">
              <FlexRender
                :render="cell.column.columnDef.cell"
                :props="cell.getContext()"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/variables";
@import "@/assets/styles/utilities";

.advanced-table-wrapper {
  display: flex;
  gap: 16px;
  flex-direction: column;

  .table-top {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .table-top-header {
      .table-top-title {
        color: $colorN80;
        font-size: 16px;
        font-weight: 700;
        line-height: 20px;
        letter-spacing: -0.05px;
        margin-bottom: 4px;
      }
  
      .table-top-text {
        font-size: 16px;
        font-weight: 400;
        line-height: 24px;
        letter-spacing: -0.05px;
        color: $colorN80;
      }
    }

    &-btn {
      max-width: fit-content;
    }
  }

  .filters-wrapper {
    display: flex;
    flex-direction: row;
    gap: 12px;
    justify-content: center;
    align-items: center;

    .table-top-btn {
      &.secondary {
        border: 0.5px solid $colorN30 !important;
        color: $colorN80; 
      }
      &.secondary-magenta {
        border: 1px solid $colorM50 !important; 
      }
    }

    hr.line {
      width: 1px;
      height: 32px;
      background-color: $colorN40;
      margin: 0 !important;
    }

    .dropdownSelectable-btn.sm {
      padding-top: 7px !important;
      padding-bottom: 7px !important;
    }
  }

  .loading {
    height: 128px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .empty-table {
    gap: 24px;
    display: flex;
    padding: 64px 0;
    align-self: stretch;
    align-items: center;
    flex-direction: column;
    border-radius: 8px;

    img {
      max-width: 120px;
    }

    .title {
      font-size: 16px;
      font-weight: 700;
      line-height: 20px;
      letter-spacing: -0.05px;
      color: $colorN80;
    }
  }
}

.table-wrapper {
  width: 100%;
  position: relative;
  border: 1px solid $colorN50;
  border-radius: 8px;
  border-spacing: 0;
  border-collapse: separate;
  padding: 0;
  overflow: hidden;

  .empty-table {
    display: flex;
    padding: 64px 0;
    align-items: center;
    flex-direction: column;

    img {
      max-width: 120px;
      margin-bottom: 24px;
    }

    > span {
      display: block;
      font-size: 16px;
      line-height: 20px;
      letter-spacing: -0.05px;
      color: $colorN80;

      &:first-of-type {
        font-weight: 700;
        margin-bottom: 12px;
      }

      &:last-of-type {
        font-weight: 600;
        margin-bottom: 16px;
      }
    }

    button {
      width: 193px;
      gap: 4px;

      img {
        margin: auto 0;
      }
    }
  }
}

.actions-row {
  display: flex;
  width: 100%;
  justify-content: space-between;
}

.advanced-table {
  width: 100%;
  border-radius: 8px;
  border-collapse: collapse;
  table-layout: fixed;

  thead {
    background: $colorD10;
    color: $colorN80;

    tr {
      height: 32px;

      th {
        padding: 6px 12px;
        text-align: left;
        vertical-align: middle;
        font-size: 13px;
        font-weight: 600;
        line-height: 18px;
        letter-spacing: -0.05px;
        color: $colorN80;
        background-color: $colorBe20;
        border-bottom: 2px solid $colorN30;

        &:not(:last-child) {
          border-right: 1px solid $colorN30;
        }
      }
    }
  }

  tbody {
    tr {
      td {
        background-color: $colorN00;
        height: 90px;
        vertical-align: middle;

        > {
          div {
            margin: auto;
          }

          label {
            display: block;
            margin: 0 8px;
          }

          .product-info {
            width: 256px;
            padding: 0 12px;
          }

          .popover-wrapper {
            padding: 0 16px;
          }

          .checkbox {
            display: flex;
            justify-content: center;
          }
        }

        &:not(:last-child) {
          border-right: 1px solid $colorN30;
        }
      }

      &.disabled {
        td {
          background-color: $colorD05;
        }
      }

      &.highlighted {
        td {
          background-color: $colorB05;
        }
      }

      &:not(:last-child) {
        td {
          border-bottom: 2px solid $colorN30;
        }
      }

      &:last-child {
        td :deep(.tooltip-text) {
          top: 60px !important;
        }
      }
    }
  }
}

.pagination {
  display: flex;
  width: 100%;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;

  .simple-select-wrapper {
    display: flex;
    flex-direction: row;
    gap: 8px;
    align-items: center;

    p {
      color: $colorN80;
      font-size: 12px;
      line-height: 16px;
      white-space: nowrap;
    }
  }
}

.buttons {
  display: flex;
  gap: 12px;
}
</style>
